// apps/web/src/widgets/branch-variant/format-override-value.ts
//
// Phase 7 — branch-variant override-field display formatter. PURE helpers —
// testable without DOM. NO React import; pairs with renderer.tsx (Task 2).
//
// toTitleCase converts override-field keys (camelCase / kebab-case /
// snake_case) to display-friendly Title Case (UI-SPEC line 277).
//
// renderOverrideValue formats override-field VALUES with XSS-safe defaults:
//   - String(value) for primitives (React auto-escapes when rendered as JSX
//     text child)
//   - "…" (U+2026) for objects / arrays + title=JSON.stringify(value) for
//     power-user inspection (React auto-escapes the title attribute)
//   - "—" (U+2014) fallback for non-renderable types (function / symbol /
//     bigint) and circular structures (JSON.stringify throws)
//
// NO markdown rendering — per RESEARCH §Anti-Patterns line 627 + UI-SPEC line
// 277 this is the XSS defense layer for author-supplied override fields.
// NO dangerouslySetInnerHTML in this file or in the renderer that consumes it.

// Split on `-` and `_` AND at every camelCase boundary (lowercase→Uppercase
// transition) so camelCase / kebab-case / snake_case all yield word lists.
// `(?<=[a-z])(?=[A-Z])` is the camelCase boundary form — it MUST require a
// lowercase predecessor; bare `(?=[A-Z])` would split runs of uppercase letters
// (e.g. "ALL_CAPS" → ["A","L","L","C","A","P","S"]) which is wrong.
const TITLECASE_SPLIT_RE = /[-_]|(?<=[a-z])(?=[A-Z])/;

export function toTitleCase(key: string): string {
  if (key.length === 0) return "";
  return key
    .split(TITLECASE_SPLIT_RE)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export interface RenderedOverrideValue {
  readonly text: string;
  readonly title?: string;
}

export function renderOverrideValue(value: unknown): RenderedOverrideValue {
  // Sentinel primitives first — String() would coerce undefined to the
  // literal "undefined", which is not the placeholder we want per UI-SPEC.
  // WR-3: null is "no value" on an author surface, NOT the raw token "null" —
  // a leaked primitive reads worse than the explicit-null-vs-omitted nuance, so
  // null maps to the same "—" missing floor undefined uses.
  if (value === undefined) return { text: "—" };
  if (value === null) return { text: "—" };

  const typeOf = typeof value;

  // Non-renderable types — JSON.stringify either drops them (function),
  // throws (bigint), or coerces to undefined (symbol). Fail-soft.
  if (typeOf === "function" || typeOf === "symbol" || typeOf === "bigint") {
    return { text: "—" };
  }

  // number / boolean / string — String() does the right thing and React
  // auto-escapes the result when interpolated as a JSX text child.
  if (typeOf === "number" || typeOf === "boolean" || typeOf === "string") {
    return { text: String(value) };
  }

  // Object / array — "…" placeholder with full value in title attribute. The
  // try/catch handles circular references (JSON.stringify throws) — real
  // recipe data shouldn't have these (it's wire-JSON), but defense-in-depth
  // keeps the render alive instead of crashing the widget tree.
  try {
    return { text: "…", title: JSON.stringify(value) };
  } catch {
    return { text: "—" };
  }
}

// ---------------------------------------------------------------------------
// Plan 11.6-04 — size-indexed override resolution helpers.
//
// A size-indexed override field (declared via recipe.variantOverrideShapes)
// carries a per-size ARRAY whose entries align positionally with a scaling-
// matrix axis's steps. The renderer resolves the active size by reading the
// scaling widget's `axisSelections[sizeAxisId]` — a STEP-ID string, never a
// numeric index — and mapping it to a position. These two helpers keep that
// resolution PURE (testable without DOM); the renderer holds the cross-widget
// React read (one top-level useContext) and calls these in its map loop.
//
// NO React import here; renderOverrideValue / toTitleCase above stay byte-
// preserved (PATTERNS decision (b): the formatter stays pure, the size-indexed
// branch lives inline in the renderer). The "—" (U+2014) sentinel is the SAME
// missing-cell floor renderOverrideValue uses.
// ---------------------------------------------------------------------------

// resolveSizeIndex — STEP-ID → POSITION. Given an axis's steps array and the
// selected step id (from scaling-matrix state.axisSelections[sizeAxisId]),
// return the position via findIndex. Returns -1 when no size is selected
// (stepId === undefined) or the persisted id no longer matches any step
// (stale selection / id drift) — both degrade to the "no active cell" render.
export function resolveSizeIndex(
  steps: ReadonlyArray<{ id: string }>,
  stepId: string | undefined,
): number {
  return stepId === undefined ? -1 : steps.findIndex((s) => s.id === stepId);
}

// formatSizeCell — resolve one positional array entry to display text. Out of
// range (idx < 0 from resolveSizeIndex, or idx >= arr.length from array/steps
// length-drift) yields the "—" sentinel — the size-cell missing floor. In
// range, a PRIMITIVE cell (string | number | boolean | bigint) is rendered via
// String() (React auto-escapes the result downstream as a JSX text child —
// same XSS posture as renderOverrideValue's primitive path).
//
// WR-03: positional override cells are author JSON (z.unknown()-class — nothing
// constrains them to primitives). A non-primitive cell (object / array / symbol
// / function / null) must NOT leak `String(obj)` → "[object Object]",
// `String([1,2])` → "1,2", or throw (`String(symbol)`). Guard those to the same
// "—" floor renderOverrideValue uses for non-renderable types, so formatSizeCell
// honors the file-header claim that it shares renderOverrideValue's discipline.
export function formatSizeCell(arr: ReadonlyArray<unknown>, idx: number): string {
  if (idx < 0 || idx >= arr.length) return "—";
  const v = arr[idx];
  const t = typeof v;
  // null is typeof "object"; object/array are typeof "object"; symbol throws
  // under String(); function stringifies to its source. All → missing floor.
  if (v === null || t === "object" || t === "function" || t === "symbol") return "—";
  return String(v); // string | number | boolean | bigint
}
